import { useLocation } from 'react-router-dom';
import React, { useRef, useState, useEffect } from 'react';
import { ACTIONS, LIFECYCLE, type StoreHelpers } from 'react-joyride';

import { useRouter } from 'src/routes/hooks';
import { waitForElement } from 'src/routes/utils';

import { dashboardWalktour } from 'src/utils/walktour/dashboard-walktour-data';

import { Walktour } from './walktour';
import { useWalktour } from './use-walktour';

import type { CustomStepCallbackProps } from './types';

function DashboardWalktour() {
  const router = useRouter();
  const location = useLocation();
  const helpersRef = useRef<StoreHelpers>();
  const setRunRef = useRef<React.Dispatch<React.SetStateAction<boolean>>>();
  const previousPathRef = useRef<string>(location.pathname);

  const [stepIndex, setStepIndex] = useState<number>(0);

  const walktourNavigateHelper = async (path: string, target: string, nextStepIndex: number) => {
    if (setRunRef.current) {
      setRunRef.current(false);
    }
    router.push(path);

    await waitForElement(target, 10000);

    setTimeout(() => {
      setStepIndex(nextStepIndex);
      if (setRunRef.current) {
        setRunRef.current(true);
      }
    }, 300);
  };

  // Helper function to get the path for a step (with fallback logic)
  const getStepPath = (step: (typeof dashboardWalktour.steps)[number]): string | undefined => {
    if (step.stepPath) {
      return step.stepPath;
    }
    // Fallback: infer from submitFromPath or submitToPath
    if (step.submitFromPath) {
      return step.submitFromPath;
    }
    if (step.submitToPath) {
      return step.submitToPath;
    }
    return undefined;
  };

  const handleCallback = async (data: CustomStepCallbackProps) => {
    const { index, lifecycle, action, step } = data;

    if (
      step.navigationPath !== undefined &&
      action === ACTIONS.NEXT &&
      lifecycle === LIFECYCLE.COMPLETE
    ) {
      const nextStepIndex = index + 1;
      const nextStep = dashboardWalktour.steps[nextStepIndex];
      const nextTarget = nextStep?.target as string;
      await walktourNavigateHelper(step.navigationPath!, nextTarget, nextStepIndex);
    } else if (action === ACTIONS.PREV && lifecycle === LIFECYCLE.COMPLETE) {
      // Handle back navigation - check if we need to navigate to previous step's page
      const prevStepIndex = index - 1;
      const prevStep = dashboardWalktour.steps[prevStepIndex];

      if (prevStep) {
        const prevStepPath = getStepPath(prevStep);

        if (prevStepPath && prevStepPath !== location.pathname) {
          // Previous step is on a different page, navigate there first
          const prevTarget = prevStep.target as string;
          await walktourNavigateHelper(prevStepPath, prevTarget, prevStepIndex);
        } else {
          // Previous step is on the same page, just go back
          setStepIndex(prevStepIndex);
        }
      }
    } else if (lifecycle === LIFECYCLE.COMPLETE && action === ACTIONS.NEXT) {
      setStepIndex(index + 1);
    } else if (action === ACTIONS.SKIP) {
      if (setRunRef.current) {
        setRunRef.current(false);
      }
      setStepIndex(0);
    }
  };

  const walktour = useWalktour({ ...dashboardWalktour, onCallback: handleCallback });

  // Store setRun in ref so we can use it in the callback
  useEffect(() => {
    setRunRef.current = walktour.setRun;
  }, [walktour.setRun]);

  useEffect(() => {
    const handler = () => {
      walktour.setRun(true);
      console.log(walktour);
    };

    window.addEventListener('walktour:start:dashboard', handler);

    return () => {
      window.removeEventListener('walktour:start:dashboard', handler);
    };
  }, [walktour]);

  // Handle form submission detection
  useEffect(() => {
    const currentStep = dashboardWalktour.steps[stepIndex];
    const isWaitingForSubmit = currentStep?.waitForSubmit === true;
    const submitFromPath = currentStep?.submitFromPath;
    const submitToPath = currentStep?.submitToPath;
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // If we're waiting for submit and navigated from submitFromPath to submitToPath
    if (
      isWaitingForSubmit &&
      submitFromPath &&
      submitToPath &&
      previousPath === submitFromPath &&
      currentPath === submitToPath &&
      walktour.run
    ) {
      // Form was submitted successfully, proceed to next step
      setTimeout(() => {
        setStepIndex(stepIndex + 1);
        if (setRunRef.current) {
          setRunRef.current(true);
        }
      }, 500);
    }

    // Update previous path only if we're not in the middle of waiting for submit
    // or if we've moved to a different page
    if (!isWaitingForSubmit || currentPath !== previousPath) {
      previousPathRef.current = currentPath;
    }
  }, [location.pathname, stepIndex, walktour.run]);

  const currentStep = dashboardWalktour.steps[stepIndex];
  const isWaitingForSubmit = currentStep?.waitForSubmit === true;

  return (
    <Walktour
      run={walktour.run}
      steps={walktour.steps}
      callback={walktour.onCallback}
      stepIndex={stepIndex}
      styles={
        isWaitingForSubmit
          ? {
              overlay: {
                pointerEvents: 'none',
              },
              spotlight: {
                pointerEvents: 'none',
              },
            }
          : undefined
      }
      getHelpers={(storeHelpers: StoreHelpers) => {
        helpersRef.current = storeHelpers;
        walktour.setHelpers(storeHelpers);
      }}
    />
  );
}

export default DashboardWalktour;
